import { useEffect } from 'react'

import { useLoading } from './useLoading'

/**
 * Hook for providers to register their loading state with the global loading system.
 * Automatically registers on mount and unregisters on unmount.
 *
 * @param name - Unique identifier for the provider (e.g., 'config', 'kinde')
 * @param isLoading - The current loading state of the provider
 *
 * @example
 * ```tsx
 * export function MyProvider({ children }) {
 *   const [loading, setLoading] = useState(true)
 *   useRegisterLoadingState('myProvider', loading)
 *   // ... rest of provider logic
 * }
 * ```
 */
export default function useRegisterLoadingState(name: string, isLoading: boolean): void {
  const { registerLoadingState, unregisterLoadingState } = useLoading()

  useEffect(() => {
    // Register the loading state
    registerLoadingState(name, isLoading)

    // Cleanup: unregister on unmount
    return () => {
      unregisterLoadingState(name)
    }
  }, [name, isLoading, registerLoadingState, unregisterLoadingState])
}

